import * as m from '@staiter/i18n';
import { type BomCreateInput, BomItemInput, BomOperationInput } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '~/components/data';
import { ItemPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, Input, Select, WizardStepper } from '~/components/primitives';
import { applyValidationErrors } from '~/lib/api/apply-validation-errors';
import { createBom } from '~/lib/api/boms';
import { ApiRequestError } from '~/lib/api/client';
import { fetchBoms, fetchItem } from '~/lib/api/inventory';
import { useZodForm } from '~/lib/forms/useZodForm';
import shared from './_auth._shared.module.css';
import styles from './_auth.boms.new.module.css';

export const Route = createFileRoute('/_auth/boms/new')({ component: NewBom });

const DecimalStr = z
  .string()
  .min(1, m.route_bom_new_err_required())
  .regex(/^-?\d+(\.\d+)?$/, m.route_bom_new_err_format());

const MaterialSchema = z.object({
  item_id: z.string().uuid(m.route_bom_new_err_item_required()),
  qty: DecimalStr,
  uom: z.string().min(1),
  scrap_pct: z.string().default('0'),
  is_alternate: z.boolean().default(false),
});

const OperationSchema = z.object({
  operation_name: z.string().min(1, m.route_bom_new_err_operation_name_required()),
  time_minutes: DecimalStr,
  workstation: z.string().default(''),
  cost_per_minute: z.string().default(''),
});

const FormSchema = z.object({
  bom_template_key: z.string().min(1, m.route_bom_new_err_code_required()),
  name: z.string().min(1, m.route_bom_new_err_name_required()),
  item_id: z.string().uuid(m.route_bom_new_err_fg_required()),
  output_qty: DecimalStr,
  uom: z.string().min(1),
  version: z.coerce.number().int().min(1).default(1),
  /**
   * F15 (project-plan-change §C) — активировать сразу.
   *
   * Умолчание ВКЛЮЧЕНО. Прогон подтвердил, что активация не плодит вторую активную
   * версию: инвариант держит частичный уникальный индекс
   * `boms_active_template_unq`, а сервер теперь отвечает на коллизию 409 с именем
   * соперника вместо прежнего 500. Без активации мастер вёл в тупик после успеха —
   * производственный заказ отвергает неактивную спецификацию (`INACTIVE_BOM`).
   */
  activate_now: z.boolean().default(true),
  materials: z.array(MaterialSchema).min(1, m.route_bom_new_err_materials_min()),
  operations: z.array(OperationSchema).default([]),
});
type FormValues = z.infer<typeof FormSchema>;

const UOM_OPTIONS = (): { value: string; label: string }[] => [
  { value: 'pcs', label: m.route_bom_new_uom_pcs() },
  { value: 'kg', label: m.route_bom_new_uom_kg() },
  { value: 'g', label: m.route_bom_new_uom_g() },
  { value: 'm', label: m.route_bom_new_uom_m() },
  { value: 'm2', label: m.route_bom_new_uom_m2() },
  { value: 'l', label: m.route_bom_new_uom_l() },
  { value: 'set', label: m.route_bom_new_uom_set() },
];

type Step = 1 | 2 | 3;

function NewBom(): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>(1);

  const form = useZodForm(FormSchema, {
    bom_template_key: '',
    name: '',
    item_id: '',
    output_qty: '1',
    uom: 'pcs',
    version: 1,
    activate_now: true,
    materials: [{ item_id: '', qty: '', uom: 'pcs', scrap_pct: '0', is_alternate: false }],
    operations: [],
  });
  const { control, handleSubmit, register, getValues, trigger, watch, setValue, formState } = form;
  const { errors, isValid } = formState;

  const materialsArr = useFieldArray({ control, name: 'materials' });
  const operationsArr = useFieldArray({ control, name: 'operations' });

  // FE-115 Sprint Q+: auto-fill UoM from item.default_uom when item selected.
  const watchedItemId = useWatch({ control, name: 'item_id' });
  const itemQuery = useQuery({
    queryKey: ['item', watchedItemId],
    queryFn: () => fetchItem(watchedItemId),
    enabled: !!watchedItemId,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (itemQuery.data?.default_uom) {
      const current = getValues('uom');
      // Only override if user hasn't manually changed from 'pcs' default.
      if (current === 'pcs') {
        setValue('uom', itemQuery.data.default_uom, { shouldValidate: false });
      }
    }
  }, [itemQuery.data, getValues, setValue]);

  const createMut = useMutation({
    mutationFn: (data: FormValues) => {
      const input: BomCreateInput = {
        bom_template_key: data.bom_template_key.trim(),
        name: data.name.trim(),
        item_id: data.item_id,
        output_qty: data.output_qty,
        uom: data.uom,
        version: data.version,
        is_active: data.activate_now,
        items: data.materials.map((mat, i) =>
          BomItemInput.parse({
            item_id: mat.item_id,
            qty: mat.qty,
            uom: mat.uom,
            position: i,
            scrap_pct: mat.scrap_pct || undefined,
            is_alternate: mat.is_alternate,
          }),
        ),
        operations: data.operations.map((o, i) =>
          BomOperationInput.parse({
            operation_name: o.operation_name.trim(),
            time_minutes: Number.parseFloat(o.time_minutes) || 0,
            position: i,
            workstation: o.workstation.trim() || undefined,
            cost_per_minute: o.cost_per_minute.trim() || undefined,
          }),
        ),
      };
      return createBom(input);
    },
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_bom_new_toast_created() });
      void qc.invalidateQueries({ queryKey: ['boms'] });
      nav({ to: '/boms/$bomId', params: { bomId: res.id } });
    },
    onError: (err: Error) => {
      /**
       * F15 — коллизия активных версий не должна стоить человеку трёх шагов
       * мастера. Пересоздаём ЧЕРНОВИКОМ и говорим, что произошло и где решать:
       * какая версия рабочая — решение человека, а не мастера.
       */
      if (
        err instanceof ApiRequestError &&
        err.code === 'resource.conflict' &&
        (err.details as { reason?: string } | undefined)?.reason === 'active_version_exists'
      ) {
        toast.push({
          variant: 'info',
          title: m.route_bom_new_activate_conflict(),
        });
        setValue('activate_now', false);
        createMut.mutate({ ...getValues(), activate_now: false });
        return;
      }
      if (applyValidationErrors(form, err)) return;
      toast.push({
        variant: 'error',
        title: m.route_bom_new_toast_create_failed(),
        description: err.message,
      });
    },
  });

  const goNext = async (): Promise<void> => {
    if (step === 1) {
      const ok = await trigger(['bom_template_key', 'name', 'item_id', 'output_qty', 'uom']);
      if (ok) setStep(2);
      return;
    }
    if (step === 2) {
      const ok = await trigger(['materials', 'operations']);
      if (ok) setStep(3);
      return;
    }
    // step 3
    const data = getValues();
    createMut.mutate(data);
  };
  const goBack = (): void => {
    if (step > 1) setStep((step - 1) as Step);
  };

  // Review values (watch for live sync in step 3)
  const reviewValues = watch();

  /**
   * F82 (PPC-F7, пак tails-closeout) — «Активировать сразу» стояло включённым без
   * предпросмотра: активна ли уже версия этого кода, мастер не знал. При активной
   * сервер отвечал 409, и мастер пересоздавал запись черновиком ПОСЛЕ трёх шагов.
   * Теперь шаг обзора спрашивает заранее, называет активную версию и снимает
   * галочку сам — две активные версии одного кода не допускаются.
   */
  const reviewKey = (reviewValues.bom_template_key ?? '').trim();
  const activeBomQuery = useQuery({
    queryKey: ['boms', 'active-by-key', reviewKey],
    queryFn: () => fetchBoms({ template_key: reviewKey, is_active: 'true' }),
    enabled: step === 3 && reviewKey !== '',
  });
  const activeBom = activeBomQuery.data?.items[0];
  useEffect(() => {
    if (activeBom) setValue('activate_now', false);
  }, [activeBom, setValue]);

  return (
    <form className={styles.container} onSubmit={handleSubmit((d) => createMut.mutate(d))}>
      <PageHeader title={m.route_bom_new_title()} description={m.route_bom_new_description()} />

      <WizardStepper
        ariaLabel={m.route_bom_new_stepper_aria()}
        current={step}
        steps={[
          { n: 1, label: m.route_bom_new_step1_label() },
          { n: 2, label: m.route_bom_new_step2_label() },
          { n: 3, label: m.route_bom_new_step3_label() },
        ]}
      />

      {step === 1 && (
        <section className={styles.section}>
          {/* FE-112 Sprint Q+: removed duplicate h2 (WizardStepper уже даёт label). */}
          <div className={shared.formGrid2}>
            <Input
              label={m.route_bom_new_field_code_label()}
              required
              placeholder={m.route_bom_new_field_code_placeholder()}
              hint={m.route_bom_new_field_code_hint()}
              className={styles.mono}
              {...register('bom_template_key')}
              error={errors.bom_template_key?.message}
            />
            <Input
              label={m.route_bom_new_field_name_label()}
              required
              placeholder={m.route_bom_new_field_name_placeholder()}
              {...register('name')}
              error={errors.name?.message}
            />
            <Controller
              control={control}
              name="item_id"
              render={({ field, fieldState }) => (
                <ItemPicker
                  label={m.route_bom_new_field_fg_label()}
                  required /* FE-113 Sprint Q+: required `*` для mandatory finished good. */
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Input
              label={m.route_bom_new_field_output_qty_label()}
              required
              inputMode="decimal"
              hint={m.route_bom_new_field_output_qty_hint()}
              className={styles.mono}
              {...register('output_qty')}
              error={errors.output_qty?.message}
            />
            <Controller
              control={control}
              name="uom"
              render={({ field }) => (
                <Select
                  label={m.route_bom_new_field_uom_label()}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={UOM_OPTIONS()}
                />
              )}
            />
            <Input
              label={m.route_bom_new_field_version_label()}
              type="number"
              hint={m.route_bom_new_field_version_hint()}
              className={styles.mono}
              {...register('version', { valueAsNumber: true })}
              error={errors.version?.message}
            />
          </div>
        </section>
      )}

      {step === 2 && (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>{m.route_bom_new_materials_heading()}</h2>
            <div className={shared.scrollX}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.colItem}>{m.route_bom_new_col_item()}</th>
                    <th className={styles.colQty}>{m.route_bom_new_col_qty()}</th>
                    <th className={styles.colUom}>{m.route_bom_new_col_uom()}</th>
                    <th className={styles.colScrap}>{m.route_bom_new_col_scrap()}</th>
                    <th className={styles.colAlt}>{m.route_bom_new_col_alt()}</th>
                    <th className={styles.removeCell} />
                  </tr>
                </thead>
                <tbody>
                  {materialsArr.fields.map((f, i) => (
                    <tr key={f.id}>
                      <td>
                        <Controller
                          control={control}
                          name={`materials.${i}.item_id` as const}
                          render={({ field, fieldState }) => (
                            <ItemPicker
                              label=""
                              value={field.value}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </td>
                      <td>
                        <Input
                          label=""
                          inputMode="decimal"
                          className={styles.mono}
                          {...register(`materials.${i}.qty` as const)}
                          error={errors.materials?.[i]?.qty?.message}
                        />
                      </td>
                      <td>
                        <Controller
                          control={control}
                          name={`materials.${i}.uom` as const}
                          render={({ field }) => (
                            <Select
                              label=""
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              options={UOM_OPTIONS()}
                              size="sm"
                            />
                          )}
                        />
                      </td>
                      <td>
                        <Input
                          label=""
                          inputMode="decimal"
                          className={styles.mono}
                          {...register(`materials.${i}.scrap_pct` as const)}
                        />
                      </td>
                      <td>
                        <Controller
                          control={control}
                          name={`materials.${i}.is_alternate` as const}
                          render={({ field }) => (
                            <Checkbox
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              label={m.route_bom_new_alt_label()}
                            />
                          )}
                        />
                      </td>
                      <td className={styles.removeCell}>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          leadingIcon={<Trash2 size={14} />}
                          onClick={() => materialsArr.remove(i)}
                          aria-label={m.route_bom_new_btn_remove()}
                          disabled={materialsArr.fields.length <= 1}
                        >
                          {''}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leadingIcon={<Plus size={14} />}
                onClick={() =>
                  materialsArr.append({
                    item_id: '',
                    qty: '',
                    uom: 'pcs',
                    scrap_pct: '0',
                    is_alternate: false,
                  })
                }
              >
                {m.route_bom_new_btn_add_material()}
              </Button>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>{m.route_bom_new_operations_heading()}</h2>
            {operationsArr.fields.length === 0 ? (
              <p className={styles.hint}>{m.route_bom_new_operations_hint()}</p>
            ) : (
              <div className={shared.scrollX}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.colOpName}>{m.route_bom_new_col_op_name()}</th>
                      <th className={styles.colTime}>{m.route_bom_new_col_time()}</th>
                      <th className={styles.colStation}>{m.route_bom_new_col_station()}</th>
                      <th className={styles.colCost}>{m.route_bom_new_col_cost()}</th>
                      <th className={styles.removeCell} />
                    </tr>
                  </thead>
                  <tbody>
                    {operationsArr.fields.map((f, i) => (
                      <tr key={f.id}>
                        <td>
                          <Input
                            label=""
                            placeholder={m.route_bom_new_op_name_placeholder()}
                            {...register(`operations.${i}.operation_name` as const)}
                            error={errors.operations?.[i]?.operation_name?.message}
                          />
                        </td>
                        <td>
                          <Input
                            label=""
                            inputMode="decimal"
                            className={styles.mono}
                            {...register(`operations.${i}.time_minutes` as const)}
                            error={errors.operations?.[i]?.time_minutes?.message}
                          />
                        </td>
                        <td>
                          <Input
                            label=""
                            placeholder={m.route_bom_new_workstation_placeholder()}
                            {...register(`operations.${i}.workstation` as const)}
                          />
                        </td>
                        <td>
                          <Input
                            label=""
                            inputMode="decimal"
                            className={styles.mono}
                            {...register(`operations.${i}.cost_per_minute` as const)}
                          />
                        </td>
                        <td className={styles.removeCell}>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            leadingIcon={<Trash2 size={14} />}
                            onClick={() => operationsArr.remove(i)}
                            aria-label={m.route_bom_new_btn_remove()}
                          >
                            {''}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leadingIcon={<Plus size={14} />}
                onClick={() =>
                  operationsArr.append({
                    operation_name: '',
                    time_minutes: '',
                    workstation: '',
                    cost_per_minute: '',
                  })
                }
              >
                {m.route_bom_new_btn_add_operation()}
              </Button>
            </div>
          </section>
        </>
      )}

      {step === 3 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>{m.route_bom_new_review_heading()}</h2>
          <div className={styles.reviewGroup}>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_code()}</span>
              <span className={[styles.reviewValue, styles.mono].join(' ')}>
                {reviewValues.bom_template_key}
              </span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_name()}</span>
              <span className={styles.reviewValue}>{reviewValues.name}</span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_output_qty()}</span>
              <span className={[styles.reviewValue, styles.mono].join(' ')}>
                {reviewValues.output_qty}{' '}
                {UOM_OPTIONS().find((u) => u.value === reviewValues.uom)?.label ?? reviewValues.uom}
              </span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_version()}</span>
              <span className={[styles.reviewValue, styles.mono].join(' ')}>
                v{reviewValues.version}
              </span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_materials()}</span>
              <span className={[styles.reviewValue, styles.mono].join(' ')}>
                {materialsArr.fields.length}
              </span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>{m.route_bom_new_review_operations()}</span>
              <span className={[styles.reviewValue, styles.mono].join(' ')}>
                {operationsArr.fields.length}
              </span>
            </div>
          </div>
          {/**
           * F15 — выбор стоит ЗДЕСЬ, на шаге обзора, рядом с решением «создавать».
           * Прежде мастер молча слал `is_active: false`, и человек узнавал об этом
           * на следующем экране — отказом производственного заказа.
           */}
          {activeBom && (
            <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
              {m.route_bom_new_active_exists({
                version: String(activeBom.version ?? ''),
                name: activeBom.name,
              })}
            </div>
          )}
          <Checkbox
            label={m.route_bom_new_activate_label()}
            checked={reviewValues.activate_now}
            disabled={activeBom !== undefined}
            {...(activeBom
              ? {
                  description: m.route_bom_new_activate_blocked({
                    version: String(activeBom.version ?? ''),
                  }),
                }
              : {})}
            onChange={(e) => setValue('activate_now', e.target.checked)}
          />
          {/* F18 гейт (обе половины): при активной версии синий блок повторял
              янтарное предупреждение и уходил под закреплённый низ. */}
          {!activeBom && (
            <div className={[shared.inlineAlert, shared.inlineAlertInfo].join(' ')}>
              {reviewValues.activate_now
                ? m.route_bom_new_activate_hint()
                : [
                    m.route_bom_new_review_draft_notice_prefix(),
                    m.route_bom_new_review_draft_notice_emphasis(),
                    m.route_bom_new_review_draft_notice_suffix(),
                  ].join('')}
            </div>
          )}
        </section>
      )}

      <div className={`${styles.footer} ${shared.stickyFooter}`}>
        <div>
          {step > 1 && (
            <Button
              type="button"
              variant="ghost"
              onClick={goBack}
              leadingIcon={<ArrowLeft size={14} />}
            >
              {m.route_bom_new_btn_back()}
            </Button>
          )}
        </div>
        <div className={styles.footerRight}>
          <Button type="button" variant="ghost" onClick={() => nav({ to: '/boms' })}>
            {m.route_bom_new_btn_cancel()}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void goNext()}
            disabled={(step === 3 && !isValid) || createMut.isPending}
            loading={createMut.isPending && step === 3}
            leadingIcon={step === 3 ? <Save size={14} /> : undefined}
            trailingIcon={step < 3 ? <ArrowRight size={14} /> : undefined}
          >
            {step === 3 ? m.route_bom_new_btn_submit() : m.route_bom_new_btn_next()}
          </Button>
        </div>
      </div>
    </form>
  );
}
